<template>
  <div class="stream-controls">
    <div v-if="isStartingStream" class="loading-overlay">
      <div class="loading-content">
        <b-spinner variant="primary" label="Carregando..." class="mb-2" />
        <div>Iniciando transmissão, aguarde...</div>
      </div>
    </div>
    <b-row>
      <b-col md="4">
        <!-- Logs da Transmissão -->
        <b-card>
          <b-card-header>
            <h5 class="mb-0">
              <feather-icon icon="ActivityIcon" class="mr-2" />
              Logs da Transmissão
            </h5>
          </b-card-header>
          
          <b-card-body>
            <div class="stream-logs">
              <div
                v-for="(log, index) in streamLogs"
                :key="index"
                class="log-item"
                :class="log.type"
              >
                <span class="log-time">{{ log.time }}</span>
                <span class="log-message">{{ log.message }}</span>
              </div>
            </div>
          </b-card-body>
        </b-card>

        <!-- Controles de Transmissão -->
        <b-card class="mt-3">
          <b-card-header>
            <h5 class="mb-0">
              <feather-icon icon="PlayIcon" class="mr-2" />
              Controles de Transmissão
            </h5>
          </b-card-header>
          
          <b-card-body>
            <div class="stream-status">
              <div class="status-indicator">
                <div 
                  class="status-dot"
                  :class="{ 
                    'status-live': isStreaming,
                    'status-offline': !isStreaming 
                  }"
                ></div>
                <span class="status-text">
                  {{ isStreaming ? 'AO VIVO' : 'OFFLINE' }}
                </span>
              </div>
            </div>
            
            <hr>
            
            <div class="control-buttons">
              <span id="btn-iniciar-transmissao-wrapper">
                <b-button
                  v-if="!isStreaming"
                  id="btn-iniciar-transmissao"
                  @click="startStream"
                  variant="success"
                  size="lg"
                  block
                  :disabled="!canStartStream"
                >
                  <feather-icon icon="PlayIcon" class="mr-2" />
                  Iniciar Transmissão
                </b-button>
              </span>
              <b-tooltip
                target="btn-iniciar-transmissao-wrapper"
                triggers="hover focus"
                v-if="!canStartStream && !isStreaming"
                placement="top"
              >
                Para iniciar a transmissão, preencha todos os campos obrigatórios e selecione um vídeo.
              </b-tooltip>

              <b-button
                v-if="isStreaming"
                @click="stopStream"
                variant="danger"
                size="lg"
                block
              >
                <feather-icon icon="SquareIcon" class="mr-2" />
                Parar Transmissão
              </b-button>
            </div>
          </b-card-body>
        </b-card>

        <!-- Envio de Mensagem no Chat -->
        <b-card v-if="isStreaming" class="mt-3">
          <b-card-header>
            <h5 class="mb-0">
              <feather-icon icon="MessageCircleIcon" class="mr-2" />
              Enviar Mensagem no Chat
            </h5>
          </b-card-header>
          
          <b-card-body>
            <b-form @submit.prevent="sendChatMessage">
              <b-form-group>
                <b-form-textarea
                  v-model="chatMessage"
                  placeholder="Digite sua mensagem..."
                  rows="3"
                  max-rows="6"
                  :disabled="sendingMessage"
                />
              </b-form-group>
              
              <b-button
                type="submit"
                variant="primary"
                size="sm"
                :disabled="!chatMessage.trim() || sendingMessage"
                :loading="sendingMessage"
                block
              >
                <feather-icon icon="SendIcon" class="mr-1" />
                {{ sendingMessage ? 'Enviando...' : 'Enviar Mensagem' }}
              </b-button>
            </b-form>
          </b-card-body>
        </b-card>
      </b-col>
      
      <b-col md="8">

        <!-- Quando NÃO estiver transmitindo -->
        <div v-if="!isStreaming" class="d-flex justify-content-center align-items-center" style="height: 300px;">
          <div class="text-center text-muted">
            <feather-icon icon="EyeOffIcon" size="32" class="mb-2" />
            <div class="mt-2 font-italic">
              Preview indisponível.<br>Inicie uma transmissão para visualizar a live e o chat.
            </div>
          </div>
        </div>

        <!-- Botão para iniciar preview (só quando não está carregando nem mostrando) -->
        <div v-if="isStreaming && !showPreview && !loadingPreview" class="d-flex justify-content-center align-items-center" style="height: 300px;">
          <b-button
            variant="info"
            size="lg"
            @click="startPreviewCountdown"
          >
            <feather-icon icon="EyeIcon" class="mr-2" />
            Ver Preview da Live
          </b-button>
        </div>

        <!-- Loading do preview com countdown -->
        <div v-if="isStreaming && loadingPreview" class="d-flex justify-content-center align-items-center" style="height: 300px;">
          <div class="text-center">
            <b-spinner variant="primary" style="width: 3rem; height: 3rem;" class="mb-3" />
            <h5 class="mb-2">Carregando Preview da Live</h5>
            <p class="text-muted">
              Aguarde {{ previewCountdown }} segundo{{ previewCountdown !== 1 ? 's' : '' }}...
            </p>
            <b-button
              variant="outline-secondary"
              size="sm"
              @click="cancelPreviewCountdown"
              class="mt-2"
            >
              <feather-icon icon="XIcon" class="mr-1" />
              Cancelar
            </b-button>
          </div>
        </div>

        <!-- Preview da Live (só aparece quando showPreview for true) -->
        <div v-if="isStreaming && showPreview">
          <!-- Live do YouTube -->
          <b-card v-if="streamId">
            <b-card-header>
              <h5 class="mb-0">
                <feather-icon icon="VideoIcon" class="mr-2" />
                Live no YouTube
              </h5>
            </b-card-header>
            <b-card-body class="p-0">
              <div class="youtube-live-container">
                <iframe
                  :src="`https://www.youtube.com/embed/${streamId}?autoplay=1`"
                  width="100%"
                  height="500"
                  frameborder="0"
                  allowfullscreen
                  allow="autoplay; encrypted-media"
                  title="Live do YouTube"
                />
              </div>
            </b-card-body>
          </b-card>

          <!-- Chat do YouTube -->
          <b-card>
            <b-card-header>
              <h5 class="mb-0">
                <feather-icon icon="MessageSquareIcon" class="mr-2" />
                Chat da Live (YouTube)
              </h5>
            </b-card-header>
            
            <b-card-body class="p-0">
              <div class="youtube-chat-container">
                <iframe
                  v-if="streamId"
                  :src="`https://www.youtube.com/live_chat?v=${streamId}&embed_domain=${embedDomain}`"
                  width="100%"
                  height="400"
                  frameborder="0"
                  allowfullscreen
                  title="Chat da Live"
                />
                <div v-else class="no-stream text-center py-4">
                  <p class="text-muted">
                    <feather-icon icon="MessageCircleIcon" class="mb-2" />
                    <br>
                    Aguardando ID da transmissão...
                  </p>
                </div>
              </div>
            </b-card-body>
          </b-card>
          <!-- Botão para ocultar o preview -->
          <div class="d-flex justify-content-end mb-2">
            <b-button
              variant="outline-secondary"
              size="sm"
              @click="showPreview = false"
            >
              <feather-icon icon="EyeOffIcon" class="mr-1" />
              Ocultar Preview da Live
            </b-button>
          </div>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BButton,
  BRow,
  BCol,
  BForm,
  BFormGroup,
  BFormTextarea,
  BBadge,
  BSpinner,
} from 'bootstrap-vue'
import { youtubeService } from '@/services/WebinarSimulator/youtubeService'

export default {
  name: 'StreamControls',
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BButton,
    BRow,
    BCol,
    BForm,
    BFormGroup,
    BFormTextarea,
    BBadge,
    BSpinner,
  },
  props: {
    streamConfig: {
      type: Object,
      required: true,
    },
    isAuthenticated: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isStreaming: false,
      isPaused: false,
      streamTime: '00:00:00',
      viewerCount: 0,
      streamLogs: [],
      streamStartTime: null,
      streamTimer: null,
      chatMessage: '',
      sendingMessage: false,
      // Chat em tempo real
      chatMessages: [],
      chatMonitoringActive: false,
      chatPollingInterval: null,
      lastMessageId: null,
      lastChatUpdate: null,
      streamId: null, // ID da transmissão do YouTube
      embedDomain: window.location.hostname, // Domínio atual
      showPreview: false, // Controla a exibição do preview da live
      isStartingStream: false,
      loadingPreview: false, // Controla o estado de loading do preview
      previewCountdown: 10, // Contador de 10 segundos
      countdownInterval: null, // Intervalo do countdown
    }
  },
  computed: {
    canStartStream() {
      console.log('=== DEBUG canStartStream ===')
      console.log('streamConfig:', this.streamConfig)
      console.log('streamKey:', this.streamConfig?.streamKey)
      console.log('videoFile:', this.streamConfig?.videoFile)
      console.log('videoUrl:', this.streamConfig?.videoUrl)
      
      const hasVideo = this.streamConfig?.videoFile || this.streamConfig?.videoUrl
      const hasStreamKey = this.streamConfig?.streamKey
      
      console.log('hasVideo:', hasVideo)
      console.log('hasStreamKey:', hasStreamKey)
      console.log('canStart:', hasStreamKey && hasVideo)
      
      return hasStreamKey && hasVideo
    },
  },
  watch: {
    isStreaming(newVal) {
      if (newVal) {
        this.fetchBroadcastId()
      }
    },
  },
  mounted() {
    this.checkStreamingStatus()
  },
  methods: {
    async startStream() {
      this.isStartingStream = true
      // Monta o FormData igual ao StreamConfig.vue
      const formData = new FormData()
      // Recupera config do localStorage
      const streamConfigStr = localStorage.getItem('stream_config')
      let config = {}
      if (streamConfigStr) {
        try {
          config = JSON.parse(streamConfigStr)
        } catch (e) {
          this.$toast.error('Erro ao ler configuração da transmissão!')
          return
        }
      }
      // Adiciona vídeo (arquivo local ou URL do AWS)
      if (this.streamConfig && this.streamConfig.videoFile) {
        // Se tem arquivo local, envia o arquivo
        formData.append('video', this.streamConfig.videoFile)
        console.log('Usando videoFile para transmissão')
      } else if (config.videoUrl) {
        // Se não tem arquivo local mas tem URL do AWS, envia a URL
        formData.append('videoUrl', config.videoUrl)
        formData.append('videoKeyName', config.videoKeyName || '')
        console.log('Usando videoUrl para transmissão:', config.videoUrl)
      }
      
      formData.append('title', config.title || '')
      formData.append('description', config.description || '')
      // formData.append('scheduledStartTime', config.scheduledStartTime || '')
      formData.append('streamKey', config.streamKey || '')
      formData.append('quality', config.quality || '')
      formData.append('bitrate', config.bitrate || '')
      formData.append('fps', config.fps || '')
      // Adiciona IA Chat
      formData.append('aiChatEnabled', config.aiChatEnabled || false)
      formData.append('aiKeyword', config.aiKeyword || '')
      // Adiciona userId
      const userData = localStorage.getItem('userData')
      if (userData) {
        formData.append('userId', JSON.parse(userData).id)
      }
      // Adiciona o campo chat
      const chatMessages = localStorage.getItem('chat_scheduled_messages')
      if (chatMessages) {
        formData.append('chat', chatMessages)
      }

      let userId = null
      if (userData) {
        userId = JSON.parse(userData).id
      }
      if (!userId) {
        this.$toast.error('Usuário não encontrado!')
        this.isStartingStream = false
        return
      }

      this.$toast.info('Iniciando live...')

      try {
        // Requisição igual ao StreamConfig.vue
        await youtubeService.createStreamWithVideo(userId)
        this.isStreaming = true
        this.streamStartTime = new Date()
        this.startTimer()
        this.addLog('success', 'Transmissão iniciada com sucesso!')
        this.$emit('stream-status', { isStreaming: true })
        
        // Inicia monitoramento do chat
        // this.startChatMonitoring()
      } catch (error) {
        this.addLog('error', `Erro ao iniciar transmissão: ${error.message || error}`)
        this.$toast.error(`Erro ao iniciar transmissão: ${error.message || error}`)
      } finally {
        this.isStartingStream = false
      }
    },

    async stopStream() {
      try {
        this.addLog('info', 'Parando transmissão...')

        const userData = localStorage.getItem('userData')
        const userId = JSON.parse(userData).id

        await youtubeService.stopStream(userId)
 
        this.isStreaming = false
        this.isPaused = false
        this.stopTimer()
        this.addLog('success', 'Transmissão parada.')

        this.$emit('stream-status', { isStreaming: false })
        
        // Para monitoramento do chat
        // this.stopChatMonitoring()
      } catch (error) {

        console.log(error)
        // Trata erro de live já encerrada
        console.log('Erro ao parar transmissão:', error)
        if (error.code === 'LIVE_ALREADY_ENDED') {
          this.isStreaming = false
          this.isPaused = false
          this.stopTimer()
          this.addLog('warning', 'A transmissão já estava encerrada.')
          this.$toast.warning('A transmissão já estava encerrada.')
          this.$emit('stream-status', { isStreaming: false })
        } else {
          this.addLog('error', `Erro ao parar transmissão: ${error.message}`)
          this.$toast.error(`Erro ao parar transmissão: ${error.message}`)
        }
      }
    },

    async checkStreamingStatus() {
      try {
        const userData = localStorage.getItem('userData')
        const userId = userData ? JSON.parse(userData).id : null
        if (!userId) return

        // Chama o service para buscar o status da live
        const response = await youtubeService.getStreamStatus(userId)
        // Ajuste conforme o retorno do backend
        if (response && response.overall.isStreaming) {
          this.isStreaming = true
          this.streamId = response.youtube.streamId || response.broadcastId || response.videoId || null
          // Se quiser, pode iniciar o timer ou outras ações aqui
        } else {
          this.isStreaming = false
          this.streamId = null
        }
      } catch (error) {
        this.isStreaming = false
        this.streamId = null
        this.addLog && this.addLog('error', `Erro ao buscar status da transmissão: ${error.message}`)
      }
    },

    async sendChatMessage() {
      if (!this.chatMessage.trim()) return

      this.sendingMessage = true
      try {
        await youtubeService.sendChatMessage(this.chatMessage.trim())
        this.addLog('success', `Mensagem enviada: "${this.chatMessage.trim()}"`)
        this.$toast.success('Mensagem enviada com sucesso!')
        this.chatMessage = ''
      } catch (error) {
        this.addLog('error', `Erro ao enviar mensagem: ${error.message}`)
        this.$toast.error(`Erro ao enviar mensagem: ${error.message}`)
      } finally {
        this.sendingMessage = false
      }
    },

    // =============================================
    // MÉTODOS DO CHAT EM TEMPO REAL
    // =============================================

    async startChatMonitoring() {
      try {
        const userData = localStorage.getItem('userData')
        const userId = userData ? JSON.parse(userData).id : null
        
        if (!userId) {
          this.addLog('warning', 'Usuário não encontrado - chat não será monitorado')
          return
        }

        // Inicia monitoramento no backend
        await youtubeService.startChatMonitoring(userId)
        
        this.chatMonitoringActive = true
        this.addLog('info', 'Monitoramento do chat iniciado')
        
        // Inicia polling para buscar mensagens
        this.startChatPolling()
        
      } catch (error) {
        this.addLog('error', `Erro ao iniciar monitoramento do chat: ${error.message}`)
        console.error('Erro ao iniciar monitoramento do chat:', error)
      }
    },

    async stopChatMonitoring() {
      try {
        // Para o polling
        this.stopChatPolling()
        
        const userData = localStorage.getItem('userData')
        const userId = userData ? JSON.parse(userData).id : null
        
        if (userId) {
          // Para monitoramento no backend
          await youtubeService.stopChatMonitoring(userId)
        }
        
        this.chatMonitoringActive = false
        this.addLog('info', 'Monitoramento do chat parado')
        
      } catch (error) {
        this.addLog('error', `Erro ao parar monitoramento do chat: ${error.message}`)
        console.error('Erro ao parar monitoramento do chat:', error)
      }
    },

    startChatPolling() {
      // Polling a cada 3 segundos
      this.chatPollingInterval = setInterval(async () => {
        await this.fetchChatMessages()
      }, 3000)
      
      // Busca mensagens imediatamente
      this.fetchChatMessages()
    },

    stopChatPolling() {
      if (this.chatPollingInterval) {
        clearInterval(this.chatPollingInterval)
        this.chatPollingInterval = null
      }
    },

    async fetchChatMessages() {
      try {
        const userData = localStorage.getItem('userData')
        const userId = userData ? JSON.parse(userData).id : null
        
        if (!userId || !this.chatMonitoringActive) return

        const response = await youtubeService.getChatMessages(userId, this.lastMessageId)
        
        if (response.messages && response.messages.length > 0) {
          // Adiciona novas mensagens
          this.chatMessages.push(...response.messages)
          
          // Atualiza o ID da última mensagem
          this.lastMessageId = response.messages[response.messages.length - 1].id
          this.lastChatUpdate = new Date()
          
          // Remove mensagens antigas (manter apenas as últimas 100)
          if (this.chatMessages.length > 100) {
            this.chatMessages = this.chatMessages.slice(-100)
          }
          
          // Faz scroll automático para a última mensagem
          this.$nextTick(() => {
            this.scrollToBottom()
          })
          
          this.addLog('info', `${response.messages.length} nova(s) mensagem(ns) no chat`)
        }
        
      } catch (error) {
        // Erro silencioso para não poluir os logs com erros de polling
        console.error('Erro ao buscar mensagens do chat:', error)
      }
    },

    scrollToBottom() {
      const chatContainer = this.$refs.chatMessages
      if (chatContainer) {
        chatContainer.scrollTop = chatContainer.scrollHeight
      }
    },

    formatMessageTime(timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp)
      return date.toLocaleTimeString('pt-BR', { 
        hour: '2-digit', 
        minute: '2-digit'
      })
    },
    
    async pauseStream() {
      try {
        // await youtubeService.pauseStream() // Implementar quando necessário
        this.isPaused = true
        this.addLog('warning', 'Transmissão pausada.')
      } catch (error) {
        this.addLog('error', `Erro ao pausar transmissão: ${error.message}`)
      }
    },
    
    async resumeStream() {
      try {
        // await youtubeService.resumeStream() // Implementar quando necessário
        this.isPaused = false
        this.addLog('info', 'Transmissão retomada.')
      } catch (error) {
        this.addLog('error', `Erro ao retomar transmissão: ${error.message}`)
      }
    },
    
    startTimer() {
      this.streamTimer = setInterval(() => {
        if (this.streamStartTime) {
          const now = new Date()
          const diff = now - this.streamStartTime
          const hours = Math.floor(diff / 3600000)
          const minutes = Math.floor((diff % 3600000) / 60000)
          const seconds = Math.floor((diff % 60000) / 1000)
          
          this.streamTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
        }
      }, 1000)
    },
    
    stopTimer() {
      if (this.streamTimer) {
        clearInterval(this.streamTimer)
        this.streamTimer = null
      }
      this.streamTime = '00:00:00'
    },
    
    addLog(type, message) {
      const time = new Date().toLocaleTimeString()
      this.streamLogs.unshift({
        type,
        time,
        message,
      })
      
      // Manter apenas os últimos 50 logs
      if (this.streamLogs.length > 50) {
        this.streamLogs = this.streamLogs.slice(0, 50)
      }
    },

    async fetchBroadcastId() {
      try {
        const userData = localStorage.getItem('userData')
        const userId = userData ? JSON.parse(userData).id : null
        if (!userId) return
        // Ajuste a rota conforme seu backend
        const response = await youtubeService.getCurrentBroadcastId(userId)
        if (response && response.broadcastId) {
          this.streamId = response.broadcastId
        } else if (response && response.streamId) {
          this.streamId = response.streamId
        } else if (response && response.videoId) {
          this.streamId = response.videoId
        }
      } catch (error) {
        this.addLog('error', `Erro ao buscar o ID da transmissão: ${error.message}`)
      }
    },

    startPreviewCountdown() {
      this.loadingPreview = true
      this.previewCountdown = 10
      
      this.countdownInterval = setInterval(() => {
        this.previewCountdown -= 1
        
        if (this.previewCountdown <= 0) {
          clearInterval(this.countdownInterval)
          this.loadingPreview = false
          this.showPreview = true
          this.addLog('success', 'Preview da live carregado!')
        }
      }, 1000)
    },

    cancelPreviewCountdown() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval)
        this.countdownInterval = null
      }
      this.loadingPreview = false
      this.previewCountdown = 10
    },
  },

  beforeDestroy() {
    this.stopTimer()
    this.stopChatPolling()
    this.cancelPreviewCountdown()
  },
}
</script>

<style lang="scss" scoped>
.loading-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(255,255,255,0.85);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
}
.loading-content {
  text-align: center;
  color: #333;
  font-size: 1.2rem;
}
.stream-controls {
  .stream-status {
    display: flex;
    align-items: center;
    margin-bottom: 1rem;
    
    .status-indicator {
      display: flex;
      align-items: center;
      margin-right: 2rem;
      
      .status-dot {
        width: 12px;
        height: 12px;
        border-radius: 50%;
        margin-right: 0.5rem;
        
        &.status-live {
          background-color: #dc3545;
          animation: pulse 2s infinite;
        }
        
        &.status-offline {
          background-color: #6c757d;
        }
      }
      
      .status-text {
        font-weight: 600;
        font-size: 1.1rem;
      }
    }
    
    .stream-info {
      display: flex;
      
      .info-item {
        margin-right: 1rem;
        
        .info-label {
          font-weight: 500;
          margin-right: 0.5rem;
        }
        
        .info-value {
          color: #6c757d;
        }
      }
    }
  }
  
  .stream-logs {
    max-height: 300px;
    overflow-y: auto;
    
    .log-item {
      padding: 0.25rem 0;
      font-size: 0.875rem;
      
      &.success {
        color: #28a745;
      }
      
      &.error {
        color: #dc3545;
      }
      
      &.warning {
        color: #ffc107;
      }
      
      &.info {
        color: #17a2b8;
      }
      
      .log-time {
        font-weight: 500;
        margin-right: 0.5rem;
      }
      
      .log-message {
        color: #6c757d;
      }
    }
  }
  
  .chat-container {
    height: 400px;
    display: flex;
    flex-direction: column;
    
    .chat-messages {
      flex: 1;
      overflow-y: auto;
      padding: 1rem;
      background-color: #f8f9fa;
      
      .chat-message {
        margin-bottom: 1rem;
        padding: 0.75rem;
        background-color: white;
        border-radius: 8px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        
        .message-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.5rem;
          
          .message-author {
            font-weight: 600;
            color: #495057;
            font-size: 0.875rem;
          }
          
          .message-time {
            font-size: 0.75rem;
            color: #6c757d;
          }
        }
        
        .message-content {
          color: #212529;
          line-height: 1.4;
          word-wrap: break-word;
        }
      }
      
      .no-messages,
      .chat-disconnected {
        text-align: center;
        padding: 2rem 1rem;
        
        .feather {
          font-size: 2rem;
          opacity: 0.5;
        }
      }
    }
    
    .chat-footer {
      background-color: #f8f9fa;
      border-top: 1px solid #dee2e6;
    }
  }
  
  .live-chat-section {
    .chat-form {
      .form-group {
        margin-bottom: 1rem;
      }
      
      textarea {
        resize: vertical;
        min-height: 80px;
      }
      
      .btn {
        min-width: 120px;
      }
    }
  }
}

@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
}
</style>